import "./reader/reader.css";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
    Button,
    Dialog,
    Icons,
    LoadingState,
    Page,
    PageTree,
    ShellSubNav,
    useFeedback,
    useMediaQuery,
    useShellSubNav,
    MOBILE_MEDIA_QUERY,
} from "../../../../ds";
import type { PageTreeLock } from "../../../../ds";
import { PageChangedEvent } from "../../../../common/events/PagesChangedEvent";
import { useServiceCallPro } from "../../../../common/services/UseServiceCall";
import { LoadError, LoadGate, PartialLocksBanner } from "../../../locks/components/LockStates";
import { useSpaceLocks } from "../../../locks/hooks/useSpaceLocks";
import { lockErrorKind } from "../../../locks/services/LockService";
import { PageTreeItem } from "../../models/PageTreeItem";
import { PageService } from "../../services/PageService";
import { PageEditorPopup } from "./editor/PageEditorPopup";
import { PageContent, useSpaceTitle } from "./PageContent";
import {
    ancestorsOf,
    countPages,
    findInTree,
    findParentInTree,
    isDescendant,
    mapGetPagesResponseToPages,
    optimisticReorder,
    optimisticReparent,
    removePageTreeItemById,
    toTreeNodes,
    treeContainsPageId,
} from "./pageTree";

const pageSvc = new PageService();

export const DeletePageQuestion = "Are you sure you want to delete this page?";
export const PageDeletedText = "Page deleted successfully!";

function pageRoute(spaceId: string, pageId: string) {
    return `/spaces/${spaceId}/pages/${pageId}`;
}

type EditorState = { mode: "add"; parentPageId?: string } | { mode: "edit"; pageId: string };

/**
 * The Pages screen (board "Ark-Page"): the space's page tree in the shell's sub-nav (a drawer on phones) and the open page.
 * The tree adds, edits, moves (menu or drag and drop) and deletes pages; the search runs on the server.
 */
export function DocumentationPage() {
    const navigate = useNavigate();
    const feedback = useFeedback();
    const subNav = useShellSubNav();
    const isPhone = useMediaQuery(MOBILE_MEDIA_QUERY);
    const { spaceId, pageId } = useParams();
    const spaceTitle = useSpaceTitle();

    const [searchValue, setSearchValue] = useState<string>("");
    const [pages, setPages] = useState<PageTreeItem[]>([]);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [totalPages, setTotalPages] = useState<number>();
    const [treeError, setTreeError] = useState<LoadError>();
    const [editor, setEditor] = useState<EditorState>();
    const [deleteTarget, setDeleteTarget] = useState<PageTreeItem>();
    const [pageTitle, setPageTitle] = useState<string>();

    const spaceLocks = useSpaceLocks(spaceId, undefined, false);
    const reloadLocks = spaceLocks.reload;
    const lockMarks = spaceLocks.marks;

    const getPagesCall = useServiceCallPro(pageSvc.getPages);
    const movePageCall = useServiceCallPro(pageSvc.movePage);
    const deletePageCall = useServiceCallPro(pageSvc.deletePage);

    /****************************
     * DATA REQUESTS
     *****************************/

    const loadPages = useCallback(
        (searchWord?: string) => {
            if (!spaceId) return;

            // the badges come from the manifest; a search only filters the tree, so it does not refetch them
            if (!searchWord) reloadLocks();
            getPagesCall
                .invoke(spaceId, searchWord)
                .then((response) => {
                    const currentPages = mapGetPagesResponseToPages(response);
                    setTreeError(undefined);
                    setPages(currentPages);
                    if (!searchWord) setTotalPages(countPages(currentPages));
                })
                .catch((error) => {
                    // a space the caller cannot read is a 404 like a space that does not exist (OrbitalPlan/18 §3)
                    const kind = lockErrorKind(error);
                    setTreeError(kind === "not-found" || kind === "identity-unavailable" ? kind : undefined);
                })
                .finally(() => setHasLoaded(true));
        },
        [spaceId, getPagesCall.invoke, reloadLocks]
    );

    const reloadTree = useCallback(() => loadPages(), [loadPages]);

    /****************************
     * EFFECTS
     *****************************/

    useEffect(() => {
        loadPages(searchValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue, spaceId]);

    useEffect(() => {
        // A page that is not in the tree is either hidden (locked) or gone: the page view's own load answers 404 and shows the
        // not-found view (or "Identity unavailable" while lock grants are unknown). Only a search result list jumps to its first hit.
        if (!spaceId || !pageId || !searchValue) return;
        const parsedPageId = parseInt(pageId, 10);
        const isInsideTree = Number.isFinite(parsedPageId) && treeContainsPageId(pages, parsedPageId);
        if (!isInsideTree) {
            const firstId = pages[0]?.pageId;
            if (firstId) navigate(pageRoute(spaceId, String(firstId)), { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pages]);

    const defaultPageId = pages.length > 0 ? pages[0].pageId : undefined;
    const currentPageId = pageId ? parseInt(pageId, 10) : undefined;

    /****************************
     * USER ACTIONS
     *****************************/

    const handleAddedPageCompleted = useCallback(
        (newPageId: number) => {
            if (spaceId) navigate(pageRoute(spaceId, `${newPageId}`));
            loadPages();
        },
        [spaceId, loadPages, navigate]
    );

    const handleMovePage = useCallback(
        (space: number, page: number, instructionValue: number) => {
            movePageCall
                .invoke(space, page, { instruction: instructionValue })
                .then(() => loadPages())
                .catch((error) => feedback.error(error, "Error!"));
        },
        [loadPages, movePageCall.invoke, feedback]
    );

    const handlePageReordered = useCallback(
        (dragged: PageTreeItem, target: PageTreeItem, position: "before" | "after") => {
            if (isDescendant(pages, dragged.pageId, target.pageId)) return;

            const targetParent = findParentInTree(pages, target.pageId);
            const targetLevelSiblings = (targetParent?.children ?? pages).filter((p) => p.pageId !== dragged.pageId);
            const targetIdx = targetLevelSiblings.findIndex((p) => p.pageId === target.pageId);
            if (targetIdx === -1) return;

            const previousPages = pages;
            setPages(optimisticReorder(pages, dragged.pageId, target.pageId, position));

            pageSvc
                .movePageTo(dragged.spaceId, dragged.pageId, {
                    targetIndex: position === "after" ? targetIdx + 1 : targetIdx,
                    newParentPageId: targetParent?.pageId ?? null,
                })
                .catch((error) => {
                    setPages(previousPages);
                    loadPages();
                    feedback.error(error, "Error!");
                });
        },
        [pages, loadPages, feedback]
    );

    const handlePageMoved = useCallback(
        (dragged: PageTreeItem, target: PageTreeItem) => {
            if (isDescendant(pages, dragged.pageId, target.pageId)) return;

            const previousPages = pages;
            setPages(optimisticReparent(pages, dragged.pageId, target.pageId));

            pageSvc
                .movePageTo(dragged.spaceId, dragged.pageId, {
                    targetIndex: target.children?.length ?? 0,
                    newParentPageId: target.pageId,
                })
                .catch((error) => {
                    setPages(previousPages);
                    loadPages();
                    feedback.error(error, "Error!");
                });
        },
        [pages, loadPages, feedback]
    );

    const handleDeleteOnConfirmClicked = useCallback(
        (space: number, page: number) => {
            deletePageCall
                .invoke(space, page)
                .then(() => {
                    const pagesCopy = [...pages];
                    const parentPage = removePageTreeItemById(pagesCopy, page);
                    setPages(pagesCopy);
                    setDeleteTarget(undefined);

                    const next = parentPage || pagesCopy[0]?.pageId;
                    if (spaceId) navigate(next ? pageRoute(spaceId, `${next}`) : `/spaces/${spaceId}/pages`);

                    feedback.success(PageDeletedText, "Success!");
                    PageChangedEvent.firePageChanged();
                })
                .catch((error) => {
                    setDeleteTarget(undefined);
                    feedback.error(error, "Error!");
                });
        },
        [deletePageCall.invoke, spaceId, pages, navigate, feedback]
    );

    const byId = useCallback((id: string) => findInTree(pages, Number(id)), [pages]);

    const treeNodes = useMemo(() => toTreeNodes(pages), [pages]);
    const treeLocks = useMemo(() => {
        const out: Record<string, PageTreeLock> = {};
        for (const [id, mark] of Object.entries(lockMarks)) out[id] = { state: mark.own ? "own" : "inherited", by: mark.lockTitle };
        return out;
    }, [lockMarks]);

    const ancestors = useMemo(
        () => (currentPageId ? (ancestorsOf(pages, currentPageId) ?? []).map((p) => ({ pageId: p.pageId, title: p.title })) : []),
        [pages, currentPageId]
    );

    const editorTrail = useMemo(() => {
        if (!editor) return undefined;
        const id = editor.mode === "edit" ? Number(editor.pageId) : editor.parentPageId ? Number(editor.parentPageId) : undefined;
        const path = id ? (ancestorsOf(pages, id) ?? []) : [];
        const titles = path.map((p) => p.title);
        if (editor.mode === "add" && id) {
            const parent = findInTree(pages, id);
            if (parent) titles.push(parent.title);
        }
        return [spaceTitle, ...titles].filter((t): t is string => !!t);
    }, [editor, pages, spaceTitle]);

    /****************************
     * HTML
     *****************************/

    if (treeError) {
        // not-found → the app's not-found view; identity-unavailable → a retry state instead of an empty tree
        return (
            <Page header={false} width="full" tabTitle={[spaceTitle]} className="ark-reader-page">
                <LoadGate error={treeError} onRetry={() => loadPages(searchValue)}>{null}</LoadGate>
            </Page>
        );
    }

    const pageCountText = totalPages !== undefined ? `${totalPages} ${totalPages === 1 ? "page" : "pages"}` : undefined;

    return (
        <Page header={false} width="full" tabTitle={[pageTitle, spaceTitle ?? spaceId]} className="ark-reader-page">
            <ShellSubNav label="Pages" meta={pageCountText}>
                <div className="ark-space-tree">
                    {subNav.placement !== "drawer" ? (
                        <div className="ark-space-tree-head">
                            <span className="ark-space-tree-mark" aria-hidden="true"><Icons.book size={16} /></span>
                            <span className="ark-space-tree-name">
                                <strong title={spaceTitle}>{spaceTitle ?? "Space"}</strong>
                                {pageCountText ? <span className="ark-caption ark-muted">{pageCountText}</span> : null}
                            </span>
                        </div>
                    ) : null}
                    <PartialLocksBanner partial={spaceLocks.partial} />
                    {!hasLoaded ? (
                        <LoadingState variant="inline" text="Loading the pages…" />
                    ) : (
                        <PageTree
                            title="Pages"
                            nodes={treeNodes}
                            selectedId={currentPageId ? String(currentPageId) : undefined}
                            onSelect={(id) => {
                                if (!spaceId) return;
                                navigate(pageRoute(spaceId, id));
                                if (subNav.placement === "drawer") subNav.close();
                            }}
                            filter={searchValue}
                            onFilter={setSearchValue}
                            locks={treeLocks}
                            onAdd={(parentId) => setEditor({ mode: "add", parentPageId: parentId })}
                            onMove={(id, direction) => {
                                const node = byId(id);
                                if (node) handleMovePage(node.spaceId, node.pageId, direction === "up" ? 1 : 0);
                            }}
                            onEdit={(id) => setEditor({ mode: "edit", pageId: id })}
                            onDelete={(id) => {
                                const node = byId(id);
                                if (node) setDeleteTarget(node);
                            }}
                            canNest={(dragged, target) => !isDescendant(pages, Number(dragged), Number(target))}
                            onNest={(dragged, target) => {
                                const d = byId(dragged);
                                const t = byId(target);
                                if (d && t) handlePageMoved(d, t);
                            }}
                            onReorder={(dragged, target, position) => {
                                const d = byId(dragged);
                                const t = byId(target);
                                if (d && t) handlePageReordered(d, t, position);
                            }}
                            emptyState={
                                searchValue ? (
                                    <p className="ark-muted">No page matches “{searchValue.trim()}”.</p>
                                ) : (
                                    <p className="ark-muted">This space has no pages yet.</p>
                                )
                            }
                            footer={<Button type="white" size="sm" icon={<Icons.plus size={16} />} text="New page" onClick={() => setEditor({ mode: "add" })} />}
                        />
                    )}
                </div>
            </ShellSubNav>

            <div className={isPhone ? "ark-reader is-phone" : "ark-reader"}>
                {!hasLoaded ? (
                    <div className="ark-reader-main">
                        <LoadingState text="Loading the space…" />
                    </div>
                ) : (
                    <PageContent
                        defaultPage={defaultPageId}
                        onSave={reloadTree}
                        lockMarks={lockMarks}
                        ancestors={ancestors}
                        onEditPage={(id) => setEditor({ mode: "edit", pageId: id })}
                        onAddPage={() => setEditor({ mode: "add" })}
                        onTitle={setPageTitle}
                        isPhone={isPhone}
                        suspendDrop={!!editor || !!deleteTarget}
                    />
                )}
            </div>

            {editor && spaceId ? (
                editor.mode === "add" ? (
                    <PageEditorPopup
                        mode="add"
                        spaceId={spaceId}
                        parentPageId={editor.parentPageId}
                        trail={editorTrail}
                        onCompleted={handleAddedPageCompleted}
                        onClose={() => setEditor(undefined)}
                    />
                ) : (
                    <PageEditorPopup
                        mode="edit"
                        spaceId={spaceId}
                        pageId={editor.pageId}
                        trail={editorTrail}
                        lockMark={lockMarks[Number(editor.pageId)]}
                        onSave={reloadTree}
                        onClose={() => setEditor(undefined)}
                    />
                )
            ) : null}

            <Dialog
                open={!!deleteTarget}
                danger
                size="sm"
                title={deleteTarget ? `Delete “${deleteTarget.title.trim()}”?` : "Delete this page?"}
                description={DeletePageQuestion}
                onClose={() => setDeleteTarget(undefined)}
                footer={
                    <>
                        <Button type="secondary" text="Cancel" onClick={() => setDeleteTarget(undefined)} />
                        <Button
                            type="danger"
                            text="Delete"
                            isBusy={deletePageCall.isLoading}
                            onClick={() => deleteTarget && handleDeleteOnConfirmClicked(deleteTarget.spaceId, deleteTarget.pageId)}
                        />
                    </>
                }
            />
        </Page>
    );
}
